import Line3D from './Line3D';
import { Matrix, MatrixMethods, MutableMatrixMethods } from './matrixMethods';
import Point3D, { Point3DInterface } from './Point3D';
import { convertDegreesToRadians } from './units';

export class Rotation {
  readonly matrix: Matrix;

  public static fromAxisAndAngle(axisOfRotation: Line3D, rotationInDegrees: number): Rotation {
    return new Rotation(Rotation.getMatrix(axisOfRotation, rotationInDegrees));
  }

  private constructor(matrix: Matrix) {
    if (matrix.length !== 4 || matrix.some((row) => row.length !== 4)) throw new Error('Expected a 4x4 matrix');
    this.matrix = matrix;
  }

  private static getMatrix(axisOfRotation: Line3D, rotationInDegrees: number): Matrix {
    const { x, y, z } = axisOfRotation.direction;

    const theta = convertDegreesToRadians(rotationInDegrees);
    const sinTheta = Math.sin(theta);
    const cosTheta = Math.cos(theta);

    const entry00 = cosTheta + x * x * (1 - cosTheta);
    const entry01 = x * y * (1 - cosTheta) - z * sinTheta;
    const entry02 = x * z * (1 - cosTheta) + y * sinTheta;
    const entry10 = y * x * (1 - cosTheta) + z * sinTheta;
    const entry11 = cosTheta + y * y * (1 - cosTheta);
    const entry12 = y * z * (1 - cosTheta) - x * sinTheta;
    const entry20 = z * x * (1 - cosTheta) - y * sinTheta;
    const entry21 = z * y * (1 - cosTheta) + x * sinTheta;
    const entry22 = cosTheta + z * z * (1 - cosTheta);

    const matrix = [
      [entry00, entry01, entry02, 0],
      [entry10, entry11, entry12, 0],
      [entry20, entry21, entry22, 0],
      [0, 0, 0, 1],
    ];
    MutableMatrixMethods.conjugateByTranslation(matrix, axisOfRotation.pointNearestOrigin);
    return matrix;
  }

  mapPoint(point: Point3DInterface): Point3D {
    return MatrixMethods.shiftPoint(this.matrix, point);
  }
}
